import type { DestroyRef, Injector } from '@angular/core';
import { ActivityEntityName, BulkActionDescriptor } from '@jms/model';
import { Command } from '@workbuddy/ui/model';
import { resolveCommandSemanticId, SemanticCommandId } from '@workbuddy/ui/services/keyboard.shortcuts';
import { buildEntityBulkActionRequest, EntityBulkActionCommandArgs, EntityBulkActionExecutor } from '@workbuddy/ui/v2/blocks/entity.bulk.actions';
import { WbActionCommand, WbActionIntent, WbActionResult } from '@workbuddy/ui/v2/components/action.bar';

export type ManageShortcutTarget = { kind: 'surface'; command: WbActionCommand } | { kind: 'bulk-action'; command: Command } | { kind: 'palette'; command: Command };

export interface ManageBulkActionIntent extends WbActionIntent {
  readonly kind: 'bulk-action';
  readonly entity: ActivityEntityName;
  readonly args: EntityBulkActionCommandArgs;
  readonly onCompleted?: () => void;
}

export interface ExecuteManageSurfaceCommandOptions {
  readonly executor: Pick<EntityBulkActionExecutor, 'execute'>;
  readonly injector: Injector;
  readonly destroyRef: DestroyRef;
}

export async function executeManageSurfaceCommand(command: WbActionCommand, options: ExecuteManageSurfaceCommandOptions): Promise<void> {

  const result = await command.run?.();
  if (!isManageBulkActionIntent(result)) return;

  await options.executor.execute({ context: 'record', entity: result.entity, args: result.args, injector: options.injector, destroyRef: options.destroyRef, onCompleted: result.onCompleted });
}

function isManageBulkActionIntent(result: WbActionResult): result is ManageBulkActionIntent {

  return !!result && result.kind === 'bulk-action' && 'entity' in result && 'args' in result;
}

/**
 * A chord names a concept, not a row. Surface actions win because the surface declared them for this
 * record; a metadata bulk action is the fallback. A field command needs a value before it can run, so
 * its chord opens the palette deep-linked into that command's value panel — the target carries the
 * command so the host can resolve which panel to land on.
 */
export function resolveManageShortcutTarget(semanticId: SemanticCommandId, surfaceCommands: readonly WbActionCommand[], bulkCommands: readonly Command[]): ManageShortcutTarget | null {

  const surfaceCommand = surfaceCommands.find(command => !command.disabled && resolveCommandSemanticId(command.id) === semanticId);
  if (surfaceCommand) return { kind: 'surface', command: surfaceCommand };

  const bulkCommand = bulkCommands.find(command => resolveCommandSemanticId(command._id, resolveDescriptor(command) ?? undefined) === semanticId);
  if (!bulkCommand) return null;

  return isImmediateBulkCommand(bulkCommand) ? { kind: 'bulk-action', command: bulkCommand } : { kind: 'palette', command: bulkCommand };
}

function isImmediateBulkCommand(command: Command): boolean {

  return command.data?.commandPalette?.settingsPanel?.kind === 'action';
}

export function buildManageBulkActionArgs(entity: ActivityEntityName, record: Record<string, unknown>, command: Command): EntityBulkActionCommandArgs | null {

  const recordId = typeof record._id === 'string' ? record._id : null;
  if (!recordId) return null;

  const commandData = resolveCommandData(command);
  const request = buildEntityBulkActionRequest(entity, [recordId], command);
  if (!request) return null;

  return { action: command._id, data: [record], commandData, bulkActionRequest: request };
}

function resolveDescriptor(command: Command): BulkActionDescriptor | null {

  return (command.data as { bulkActionDescriptor?: BulkActionDescriptor } | undefined)?.bulkActionDescriptor ?? null;
}

function resolveCommandData(command: Command): Record<string, unknown> {

  if (!command.data || typeof command.data !== 'object' || Array.isArray(command.data)) return {};
  const { bulkSettingsPanel, commandPalette, ...data } = command.data as Record<string, unknown>;
  return data;
}
